import { useEffect, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { ArrowRight, Zap, ClipboardEdit, Sparkles } from "lucide-react";

/**
 * The two-path Smart Quote landing page. Presented at /book.
 * Left column: instant-quote services (each links to /quote/instant/{key}).
 * Right column: request-a-quote services (each links to /quote/request?service=key).
 *
 * Lawn Mowing keeps its existing pricing engine at /book (legacy flow) — a
 * dedicated card in the Instant Quote column links to the legacy wizard.
 *
 * iter77: When arriving with `?service=<key>` (e.g. from a Home service card
 * tap) we AUTO-REDIRECT to the correct quote flow so the customer never has
 * to pick again. Unknown keys fall through to the Request-a-Quote form.
 */

const INSTANT_SERVICES = [
  { key: "lawn_mowing",       name: "Lawn Mowing",       description: "Weekly, bi-weekly or one-time. Instant sq-ft pricing.", legacy: true },
  { key: "bush_trimming",     name: "Bush Trimming",     description: "Per-bush pricing scales with size + height." },
  { key: "mulching",          name: "Mulching",          description: "Sq-ft × depth. Material or you-supply." },
  { key: "gutter_cleaning",   name: "Gutter Cleaning",   description: "Linear-ft pricing. 1 or 2 story." },
  { key: "pressure_washing",  name: "Pressure Washing",  description: "Concrete, brick, paver, deck." },
  { key: "junk_removal",      name: "Junk Removal",      description: "Pick truck-load fraction." },
  { key: "weed_removal",      name: "Weed Removal",      description: "Sq-ft × density." },
  { key: "rock_installation", name: "Rock Installation", description: "Sq-ft × depth. Rock type + fabric options." },
];

const REQUEST_SERVICES = [
  { key: "tree_removal",      name: "Tree Removal",      startingPrice: "Starting at $250",              description: "Requires an on-site assessment." },
  { key: "stump_grinding",    name: "Stump Grinding",    startingPrice: "Starting at $100 per stump",    description: "Depth + root spread varies." },
  { key: "sodding",           name: "Sodding",           startingPrice: "Starting at $1.50/sq ft installed", description: "Grade + delivery + install." },
  { key: "outdoor_lighting",  name: "Outdoor Lighting",  startingPrice: "Starting at $300",              description: "Wiring + fixture count." },
  { key: "landscaping",       name: "Landscaping",       startingPrice: "Starting at $500",              description: "Full-yard design projects." },
  { key: "custom_service",    name: "Something Else",    startingPrice: "Custom Quote",                  description: "Anything not listed — send us photos." },
];

// iter83: also exported for RequestQuote.jsx so the per-service form can
// show the same starting-price banner + disclaimer as the picker.
export const REQUEST_QUOTE_STARTING_PRICES = REQUEST_SERVICES.reduce(
  (acc, s) => { acc[s.key] = s.startingPrice; return acc; },
  {},
);
export const REQUEST_QUOTE_PRICE_DISCLAIMER =
  "Starting prices only. Final pricing depends on measurements, materials, labor, accessibility, and site conditions.";

// iter77: Map Home /api/services legacy keys → the correct Instant/Request
// key. Any key not in this map falls through to Request-a-Quote using the
// original key (RequestQuote handles arbitrary keys gracefully).
const SERVICE_KEY_ALIAS = {
  // legacy → instant
  hedge_trimming: "bush_trimming",
  weed_eating: "weed_removal",
  // legacy → request
  tree_trimming: "tree_removal",
  // yard_cleanup, leaf_removal, flower_planting, drainage_installation,
  // edging, fence_repair, sprinkler_repair — all route to Request Quote
  // using their own service key (server just stores the key as-is).
};

const INSTANT_KEYS = new Set(INSTANT_SERVICES.map((s) => s.key));
const REQUEST_KEYS = new Set(REQUEST_SERVICES.map((s) => s.key));

/**
 * Resolve a Home-page service key to the target route.
 * Returns { path: string, replace: true } — the caller navigates there.
 */
export function resolveServiceRoute(rawKey) {
  const key = SERVICE_KEY_ALIAS[rawKey] || rawKey;
  if (key === "lawn_mowing") return { path: "/estimate" };
  if (INSTANT_KEYS.has(key)) return { path: `/quote/instant/${key}` };
  if (REQUEST_KEYS.has(key)) return { path: `/quote/request?service=${key}` };
  // Unknown key — treat as a custom request but preserve the key so the
  // admin sees exactly what the customer asked for.
  return { path: `/quote/request?service=${encodeURIComponent(key)}` };
}


export default function BookLanding() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const rawService = sp.get("service");

  // iter77: If a service was pre-selected (e.g. from a Home page service
  // card tap), skip the two-column chooser entirely and redirect straight
  // to the appropriate quote form.
  const target = useMemo(() => (rawService ? resolveServiceRoute(rawService) : null), [rawService]);

  useEffect(() => {
    if (target) {
      navigate(target.path, { replace: true });
    }
  }, [target, navigate]);

  // While the redirect is dispatched, render nothing to avoid a flash of
  // the picker.
  if (target) return null;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.16em]">
          <Sparkles className="w-3 h-3" /> Smart Booking
        </div>
        <h1 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight text-gray-900 font-[Outfit]">
          How would you like to get a price?
        </h1>
        <p className="mt-2 text-sm text-gray-600 max-w-xl mx-auto">
          Instant quote for common services · Custom quote for anything that needs an on-site look
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Instant Quote panel */}
        <div className="rounded-3xl bg-gradient-to-br from-emerald-50 to-white border-2 border-emerald-200 p-4 sm:p-5" data-testid="landing-instant-panel">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-11 h-11 rounded-xl bg-emerald-100 text-[#047857] flex items-center justify-center">
              <Zap className="w-5 h-5" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.14em] font-extrabold text-emerald-800">Instant Quote</div>
              <div className="text-base font-extrabold text-gray-900 font-[Outfit]">Get a price in 60 seconds</div>
            </div>
          </div>
          <div className="space-y-2">
            {INSTANT_SERVICES.map((s) => (
              <Link
                key={s.key}
                to={s.legacy ? "/estimate" : `/quote/instant/${s.key}`}
                className="flex items-center gap-3 rounded-2xl bg-white hover:bg-emerald-50 border border-emerald-100 p-3 transition"
                data-testid={`landing-instant-${s.key}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-extrabold text-gray-900">{s.name}</div>
                  <div className="text-xs text-gray-500 leading-snug truncate">{s.description}</div>
                </div>
                <ArrowRight className="w-4 h-4 text-emerald-600 flex-shrink-0" />
              </Link>
            ))}
          </div>
        </div>

        {/* Request a Quote panel */}
        <div className="rounded-3xl bg-gradient-to-br from-amber-50 to-white border-2 border-amber-200 p-4 sm:p-5" data-testid="landing-request-panel">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-11 h-11 rounded-xl bg-amber-100 text-amber-800 flex items-center justify-center">
              <ClipboardEdit className="w-5 h-5" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.14em] font-extrabold text-amber-800">Request a Quote</div>
              <div className="text-base font-extrabold text-gray-900 font-[Outfit]">For complex projects</div>
            </div>
          </div>
          <div className="space-y-2">
            {REQUEST_SERVICES.map((s) => (
              <Link
                key={s.key}
                to={`/quote/request?service=${s.key}`}
                className="flex items-center gap-3 rounded-2xl bg-white hover:bg-amber-50 border border-amber-100 p-3 transition"
                data-testid={`landing-request-${s.key}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2 flex-wrap">
                    <div className="text-sm font-extrabold text-gray-900">{s.name}</div>
                    <div
                      className="text-[11px] font-extrabold text-amber-800 whitespace-nowrap"
                      data-testid={`landing-request-${s.key}-price`}
                    >
                      {s.startingPrice}
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 leading-snug truncate">{s.description}</div>
                </div>
                <ArrowRight className="w-4 h-4 text-amber-600 flex-shrink-0" />
              </Link>
            ))}
          </div>
          <p
            className="mt-3 text-[11px] text-amber-900/90 leading-relaxed font-medium"
            data-testid="landing-request-disclaimer"
          >
            {REQUEST_QUOTE_PRICE_DISCLAIMER}
          </p>
          <p className="mt-2 text-[11px] text-amber-800/70 leading-relaxed">
            Upload up to 10 photos + 1 video (60 s). We&apos;ll price it and send you a quote within 24 hours.
          </p>
        </div>
      </div>
    </div>
  );
}
